import Search from '@mui/icons-material/Search';
import { Box, Link } from '@mui/material';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { useLink } from '~/hooks/link';

interface AssetLinkProps {
  kind: string;
  mediaItemId: number | null;
  contentItemId: number | null;
  contentFolderId: number | null;
  label: string;
}

/**
 * Renders the asset name. The dashboard's resolvers return
 * contentFolderId alongside the row, so we can decide at first paint
 * whether to link to the live asset or surface a content-library
 * search fallback without a click-time round trip.
 *
 *  - Live sponsor media (kind 'sponsor') → /content/:folder/media/:id/details
 *  - Live app (any other kind) → /content/:folder/apps/:id/settings.
 *    Sponsors themselves are apps — the player reports sponsorId from the
 *    sponsor app it rendered, so a sponsor's id is its content item id;
 *    pass kind "sponsorApp" with contentItemId={sponsorId} to link one.
 *  - Deleted or id-less item → label rendered as disabled text alongside
 *                   a search icon that opens the content library
 *                   prefilled with the asset name.
 */
export const AssetLink = ({
  kind,
  mediaItemId,
  contentItemId,
  contentFolderId,
  label,
}: AssetLinkProps) => {
  const { t } = useTranslation();
  const link = useLink();

  const assetId = kind === 'sponsor' ? mediaItemId : contentItemId;

  if (assetId == null || contentFolderId == null) {
    const searchPath = link('/content/search').pathname;
    const href = `${searchPath}?search=${encodeURIComponent(label)}`;
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ color: 'text.secondary' }}>
          {label}
        </Box>
        <IconButton
          label={t('pages.playout_reports.top_assets.asset_link_unavailable')}
          href={href}
          target="_blank"
          rel="noopener"
          size="small"
          onClick={(e: MouseEvent) => e.stopPropagation()}
          sx={{ color: 'primary.dark', padding: '2px' }}
        >
          <Search fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  const path =
    kind === 'sponsor'
      ? `/content/${contentFolderId}/media/${assetId}/details`
      : `/content/${contentFolderId}/apps/${assetId}/settings`;

  return (
    <Link
      href={link(path).pathname}
      target="_blank"
      rel="noopener"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
    </Link>
  );
};
